// Catalogue namespace — RUSSIAN (Module 01 §4).
//
// Register notes, the ones a reviewer should challenge first:
//  · outsourcing → «кооперация». That is what a CIS plant calls work sent to
//    another factory; «аутсорсинг» is office vocabulary and would not be
//    recognised by a process engineer writing a routing.
//  · backflush → «обратное списание», the standard MRP term.
//  · NRV → «чистая стоимость реализации» (IFRS wording used in ru accounting).
//  · scrap on a BOM line / routing step → «% отходов» (technological loss),
//    distinct from `status`-level брак, which is rejected product.
//  · effectivity → «период действия»; superseded → «заменена». A BOM version is
//    feminine (спецификация), a routing masculine (маршрут); the shared
//    `status.*` pills are therefore kept nominal («Заменена» reads for the
//    version, which is what the badge sits on).
//  · Cost-run "release" is «утвердить» (approve a standard cost), NOT the
//    quality «допустить» used for lots in module 06. Same English word, two
//    different acts; Russian keeps them apart.

import type { Translated } from "../../../i18n-shape.js";
import type { catalogueEn } from "./catalogue.i18n.js";

export const catalogueRuDict: Translated<typeof catalogueEn> = {
  nav: {
    label: "Разделы каталога",
    products: "Продукты",
    uoms: "Единицы",
    costRuns: "Расчёты себестоимости",
    templates: "Шаблоны",
    imports: "Загрузка",
  },

  screens: {
    products: "Продукты",
    productDetail: "Продукт",
    bomEditor: "Спецификация",
    routingEditor: "Маршрут",
    uoms: "Единицы измерения",
    imports: "Загрузка из Excel",
    costRuns: "Расчёты себестоимости",
    costRunDetail: "Расчёт себестоимости",
    templates: "Шаблоны",
    floorLookup: "Поиск продукта",
  },

  imports: {
    forbiddenTitle: "Недоступно для вашей роли",
    forbiddenBody:
      "Загрузка продуктов, материалов, спецификаций и маршрутов доступна планировщикам и администраторам.",
  },

  state: {
    loading: "Загрузка…",
    errorTitle: "Что-то пошло не так",
    errorDetail: "Запрос выполнить не удалось. Попробуйте ещё раз.",
    retry: "Повторить",
    requestId: "Идентификатор запроса",
    empty: "Здесь пока пусто.",
    dismiss: "Скрыть",
    close: "Закрыть",
  },

  tracking: {
    none: "Нет",
    lot: "Партия",
    serial: "Серийный номер",
    measured_unit: "Мерная единица",
  },

  status: {
    draft: "Черновик",
    active: "Действует",
    discontinued: "Снят с производства",
    superseded: "Заменена",
    archived: "В архиве",
    computing: "Идёт расчёт",
    computed: "Рассчитано",
    compute_failed: "Расчёт не удался",
    released: "Утверждено",
    discarded: "Аннулировано",
    // Не статус на проводе: действующая версия с датой начала в будущем
    // (spec 01 §9.16, D-261). Женский род — версия.
    scheduled: "Запланирована",
  },

  attr: {
    optional: "необязательно",
    selectPlaceholder: "Выберите…",
    booleanTrue: "Да",
    booleanFalse: "Нет",
    unsupportedField: "Поле такого типа здесь не редактируется.",
    errors: {
      required: "Это поле обязательно.",
      type: "У значения неверный тип.",
      enum: "Выберите одно из допустимых значений.",
      pattern: "Значение не соответствует требуемому формату.",
      unknownKey: "Этот атрибут схемой не допускается.",
      number: "Введите число.",
      integer: "Введите целое число.",
    },
  },

  cost: {
    none: "—",
    perUnit: "за {{qty}}",
    title: "Себестоимость",
    material: "Материалы",
    labor: "Оплата труда",
    overhead: "Накладные расходы",
    outsourcing: "Кооперация",
    byProductCredit: "Зачёт побочной продукции",
    total: "Итого",
    unitCost: "Себестоимость единицы",
    breakdown: "Расшифровка",
    nrv: "Чистая стоимость реализации",
    lines: "Компоненты",
    steps: "Операции",
    credits: "Зачёты",
    apportionment: "Распределение",
  },

  costRun: {
    listTitle: "Расчёты себестоимости",
    newRun: "Новый расчёт",
    loadingList: "Загружаются расчёты…",
    listErrorTitle: "Не удалось загрузить расчёты",
    listEmptyTitle: "Расчётов пока нет",
    listEmptyBody:
      "Выполните свод нормативной себестоимости, чтобы оценить изготавливаемые продукты.",
    colAsOf: "На дату",
    colScope: "Охват",
    colFacility: "Площадка",
    colStatus: "Статус",
    colCurrency: "Валюта",
    colCreated: "Создан",
    groupWide: "По всей группе",
    scope: {
      all_make_products: "Все изготавливаемые продукты",
      selected_products: "Выбранные продукты",
    },
    forbiddenTitle: "Данные о себестоимости недоступны",
    forbiddenBody: "У вас нет прав на просмотр расчётов себестоимости.",
    createTitle: "Новый расчёт себестоимости",
    fieldAsOf: "Дата расчёта",
    fieldScope: "Охват",
    fieldFacility: "Идентификатор площадки (необязательно)",
    facilityHint: "Оставьте пустым для расчёта по всей группе.",
    fieldNote: "Примечание (необязательно)",
    fieldProductIds: "Идентификаторы продуктов",
    productIdsHint: "По одному идентификатору продукта в строке.",
    create: "Запустить расчёт",
    creating: "Запускается…",
    createError: "Не удалось запустить расчёт. Проверьте поля и попробуйте ещё раз.",
    errorProductIdsRequired:
      "Для расчёта по выбранным продуктам добавьте хотя бы один идентификатор.",
    errorAsOfRequired: "Выберите дату расчёта.",
    cancel: "Отмена",
    detailLoading: "Загружается расчёт…",
    detailErrorTitle: "Не удалось загрузить расчёт",
    paramsTitle: "Параметры",
    asOfLabel: "На дату",
    scopeLabel: "Охват",
    facilityLabel: "Площадка",
    currencyLabel: "Валюта",
    noteLabel: "Примечание",
    createdLabel: "Создан",
    releasedLabel: "Утверждён",
    computingHint: "Расчёт ещё идёт — результаты по продуктам появятся, когда он завершится.",
    failedHint: "Этот расчёт не удался. Запустите новый.",
    failure: {
      APPORTIONMENT_SUM_INVALID:
        "Остановлено на {{sku}}: распределение между сопутствующими продуктами даёт 100 % или больше, поэтому на основной продукт не остаётся себестоимости.",
      BOM_CYCLE_DETECTED:
        "Остановлено на {{sku}}: спецификация содержит саму себя — напрямую или через компонент.",
      BOM_DEPTH_EXCEEDED:
        "Остановлено на {{sku}}: вложенность спецификации превышает 12 уровней — предел для разузлования.",
    },
    failureOpenBom: "Открыть спецификацию",
    discardedHint: "Этот расчёт аннулирован и больше не может быть утверждён.",
    itemsTitle: "Себестоимость по продуктам",
    colProduct: "Продукт",
    colOldCost: "Прежняя себестоимость",
    colNewCost: "Новая себестоимость",
    colDelta: "Δ%",
    colLevel: "Уровень",
    itemsLoading: "Загружаются строки расчёта…",
    itemsErrorTitle: "Не удалось загрузить строки расчёта",
    itemsEmptyTitle: "Результатов по продуктам нет",
    itemsEmptyBody: "Этот расчёт не дал ни одной строки себестоимости.",
    itemsFilteredEmpty: "Под фильтр по Δ% не подходит ни один продукт.",
    itemsFilteredEmptyBody: "Снизьте порог Δ%, чтобы увидеть остальные строки расчёта.",
    deltaNew: "Новый",
    minDeltaLabel: "Мин. Δ%",
    minDeltaAll: "Все",
    minDeltaOption: "≥ {{pct}}%",
    loadingMore: "Загружается ещё…",
    showBreakdown: "Показать расшифровку",
    hideBreakdown: "Скрыть расшифровку",
    release: "Утвердить",
    releasing: "Утверждается…",
    releasedNotice: "Расчёт утверждён — продуктов с новой нормативной себестоимостью: {{count}}.",
    discardedNotice: "Расчёт аннулирован. Ничего не переоценено.",
    releaseConfirmTitle: "Утвердить этот расчёт себестоимости?",
    releaseConfirmBody:
      "Продуктов, которые будут переоценены по новой нормативной себестоимости: {{count}}.",
    releaseConfirm: "Утвердить",
    releaseError: "Не удалось утвердить расчёт.",
    discard: "Аннулировать",
    discarding: "Аннулируется…",
    discardConfirmTitle: "Аннулировать этот расчёт?",
    discardConfirmBody: "Расчёт будет аннулирован и больше не сможет быть утверждён.",
    discardConfirm: "Аннулировать",
    discardError: "Не удалось аннулировать расчёт.",
  },

  effectivity: {
    title: "Период действия",
    current: "Текущая",
    open: "Без окончания",
    none: "Действующей версии нет.",
    from: "С",
    to: "По",
  },

  // «Какая версия действует сегодня» — ответ резолвера §3.4 (D-261).
  effectiveToday: {
    loading: "Определяется, какая версия действует сегодня…",
    resolved: "Сегодня ({{date}}) действует версия {{version}}.",
    thisVersion: "Эта версия действует сегодня ({{date}}).",
    none: "Сегодня ({{date}}) не действует ни одна версия.",
    noneHint:
      "Планирование и расчёт себестоимости не смогут определить версию, пока какая-нибудь не будет введена в действие.",
    error: "Не удалось определить, какая версия действует сегодня.",
    open: "Открыть версию {{version}}",
  },

  // Отзыв запланированного ввода в действие (§3.3.5, D-261). Все фразы
  // строятся вокруг «версии», поэтому одинаково читаются для спецификации
  // и маршрута.
  withdraw: {
    button: "Отозвать",
    title: "Отозвать запланированный ввод в действие",
    lead: "Версия {{version}} вступает в силу {{date}}. Отзыв отправит её в архив; текущая не изменится.",
    restorePreview: "Версия {{version}} (заменена) снова станет действующей.",
    noRestorePreview:
      "Предыдущая версия не восстановится — действующей версии не будет, пока какая-нибудь не будет введена в действие.",
    notRestorablePreview:
      "Версия {{version}}, которую заменила эта, уже в архиве, поэтому отозвать эту версию нельзя. Вместо этого введите в действие новую версию.",
    unknownRestorePreview:
      "С этого экрана не видно, какая версия вернётся. Отзыв восстанавливает версию, которую заменила эта, и результат её назовёт.",
    reason: "Причина",
    reasonPlaceholder: "Почему отзывается этот ввод в действие? (обязательно)",
    reasonRequired: "Причина обязательна.",
    cancel: "Отмена",
    confirm: "Отозвать",
    withdrawing: "Отзывается…",
    close: "Закрыть",
    refusal: {
      inForce:
        "Эта версия вступила в силу {{date}}, и отозвать её уже нельзя — действующую версию можно только заменить более новой.",
      serverToday: "Сегодня в системе {{today}}.",
      notActive:
        "Эта версия уже не действующая — возможно, её уже отозвали или заменили. Экран обновлён.",
      predecessorNotRestorable:
        "Версию, которую заменила эта, восстановить уже нельзя, поэтому отзыв отклонён. Вместо этого введите в действие новую версию.",
    },
    failed: "Не удалось отозвать эту версию.",
    resultRestored: "Версия {{withdrawn}} отозвана. Версия {{restored}} снова действует.",
    resultRestoredById:
      "Версия {{withdrawn}} отозвана. Версия, которую она заменила, снова действует (id {{id}}).",
    resultNone:
      "Версия {{withdrawn}} отозвана. Действующей версии сейчас нет — введите какую-нибудь в действие, прежде чем она понадобится планированию или расчёту себестоимости.",
    openRestored: "Открыть версию {{version}}",
  },

  // Отправка версии в архив (§3.3.5, D-261 с поправкой от 2026-09-08). Как и
  // в `withdraw`, все фразы строятся вокруг «версии».
  archive: {
    button: "В архив",
    title: "Отправить эту версию в архив",
    leadDraft:
      "Версия {{version}} — черновик. Отправка в архив отменяет её: её больше нельзя будет редактировать или вводить в действие. Это действие нельзя отменить.",
    leadSuperseded:
      "Версия {{version}} заменена. В архиве она останется в истории, но вернуть её будет нельзя. Это действие необратимо.",
    resumablePreview:
      "Эта версия действует до {{date}}, а её замена ещё не вступила в силу. Сначала отзовите запланированную замену.",
    cancel: "Отмена",
    confirm: "В архив",
    archiving: "Отправляется в архив…",
    close: "Закрыть",
    refusal: {
      stillResumable:
        "Эта версия действует до {{date}} и вернётся, если отозвать запланированную замену. Сначала отзовите ту версию.",
      stillResumableNoDate:
        "Эта версия вернётся, если отозвать запланированную замену. Сначала отзовите ту версию.",
      notArchivable:
        "Статус версии теперь «{{status}}», а в архив можно отправить только черновик или заменённую версию. Экран обновлён.",
      notArchivableNoStatus:
        "Статус этой версии изменился до того, как её удалось отправить в архив. В архив можно отправить только черновик или заменённую версию. Экран обновлён.",
    },
    failed: "Не удалось отправить эту версию в архив.",
    result: "Версия {{version}} отправлена в архив.",
  },

  version: {
    label: "Версия",
    switch: "Сменить версию",
    compare: "Сравнить",
    against: "Сравнить с",
  },
  diff: {
    added: "Добавлено",
    removed: "Удалено",
    changed: "Изменено",
    unchanged: "Без изменений",
    noChanges: "Различий нет.",
    before: "Было",
    after: "Стало",
  },

  floorLookup: {
    scanPrompt: "Отсканируйте QR-код продукта.",
    scanLabel: "Отсканируйте или введите код продукта",
    scanPlaceholder: "Сканируйте QR или вставьте ссылку на продукт…",
    scanHint: "Наведите сканер на QR-код на карточке продукта или вставьте ссылку.",
    lookup: "Найти",
    clear: "Очистить",
    scanUnreadable: "Этот код прочитать не удалось. Отсканируйте QR карточки продукта ещё раз.",
    offline: "Нет связи — показан сохранённый каталог.",
    online: "Связь есть",
    syncing: "Каталог синхронизируется…",
    refresh: "Обновить",
    cacheAge: "Каталог обновлён {{age}}",
    cacheNever: "Ещё не синхронизировался",
    ageJustNow: "только что",
    ageMinutes: "{{n}} мин назад",
    ageHours: "{{n}} ч назад",
    ageDays: "{{n}} дн. назад",
    facility: "Площадка",
    changeFacility: "Сменить площадку",
    facilityPromptTitle: "Укажите площадку этого устройства",
    facilityPromptBody:
      "Выберите площадку, к которой относится этот пост, чтобы её каталог синхронизировался для работы без связи.",
    facilityPick: "Выбрать эту площадку",
    facilityCancel: "Оставить текущую площадку",
    facilityErrorTitle: "Не удалось загрузить площадки",
    facilityErrorBody: "Список площадок загрузить не удалось. Подключитесь к сети и повторите.",
    facilityEmptyTitle: "Площадок пока нет",
    facilityEmptyBody:
      "Попросите офис добавить площадку этого завода, прежде чем настраивать это устройство.",
    syncErrorTitle: "Не удалось синхронизировать каталог",
    syncErrorBody: "Каталог загрузить не удалось, а сохранённой копии на устройстве ещё нет.",
    cacheEmptyTitle: "Каталог не синхронизирован",
    cacheEmptyBody:
      "Подключитесь, чтобы синхронизировать каталог этой площадки для работы без связи.",
    notFoundTitle: "Продукт не найден",
    notFoundBody: "На этой площадке нет действующего продукта с таким кодом.",
    notFoundOfflineBody: "Этого продукта нет в сохранённой копии. Подключитесь, чтобы его найти.",
    card: {
      sku: "Артикул",
      name: "Название",
      attributes: "Атрибуты",
      noAttributes: "Атрибуты не записаны.",
      routing: "Маршрут — эта площадка",
      noRouting: "Для этой площадки действующий маршрут не сохранён.",
      step: "Операция {{no}}",
      instructions: "Инструкции",
      noInstructions: "Инструкций нет.",
      outsourced: "По кооперации",
      measured: "Фиксация измерения",
      serial: "Фиксация серийного номера",
      qcGate: "Точка контроля качества",
    },
  },

  // ---- S1 Список продуктов -------------------------------------------------
  productsList: {
    title: "Продукты",
    new: "Новый продукт",
    searchLabel: "Поиск",
    searchPlaceholder: "Артикул или название…",
    loading: "Загружаются продукты…",
    errorTitle: "Не удалось загрузить продукты",
    emptyTitle: "Продуктов пока нет",
    emptyBody: "Начните с шаблона или загрузите свой прайс-лист.",
    emptyCta: "Новый продукт",
    filterRejectedTitle: "Эти фильтры недопустимы",
    filterRejectedBody:
      "Каталог не смог обработать такое сочетание фильтров. Сбросьте их и сузьте список заново.",
    noMatchTitle: "Подходящих продуктов нет",
    noMatchBody: "Под эти фильтры не подходит ни один продукт.",
    clearFilters: "Сбросить фильтры",
    filters: {
      kind: "Вид",
      status: "Статус",
      schema: "Схема атрибутов",
      makeOrBuy: "Производство или закупка",
      attribute: "Атрибут",
      anyAttribute: "Любой атрибут",
      attributeOp: "Сравнение",
      attributeValue: "Значение",
      op: {
        eq: "равно",
        gte: "не менее",
        lte: "не более",
      },
      anyKind: "Любой вид",
      anyStatus: "Любой статус",
      anySchema: "Любая схема",
      anyMakeOrBuy: "Производство или закупка",
      clear: "Сбросить фильтры",
      more: "Фильтры",
      moreCount: "Фильтры ({{count}})",
      title: "Фильтры",
      done: "Готово",
      remove: "Убрать {{filter}}",
      chip: "{{filter}} — {{value}}",
    },
    col: {
      select: "Выбрать",
      sku: "Артикул",
      name: "Название",
      kind: "Вид",
      baseUom: "Базовая единица",
      status: "Статус",
      attributes: "Атрибуты",
      cost: "Нормативная себестоимость",
    },
    makeOrBuy: {
      make: "Производим",
      buy: "Закупаем",
      both: "И то, и другое",
    },
    bulk: {
      regionLabel: "Групповые действия",
      selectedCount: "Выбрано: {{count}}",
      selectAll: "Выбрать все",
      clear: "Снять выбор",
      activate: "Ввести в действие",
      discontinue: "Снять с производства",
      running: "Выполняется…",
      done: "Обновлено продуктов: {{count}}.",
      partial:
        "Обновлено: {{ok}}, отклонено: {{failed}}. Откройте отклонённые, чтобы узнать причину.",
      failedFallback: "Групповое обновление не выполнено. Ничего не изменилось.",
    },
    page: {
      prev: "Назад",
      next: "Вперёд",
      showing: "Показано: {{count}}",
    },
    chipMore: "ещё {{count}}",
    create: {
      pickTitle: "Создать новый продукт",
      pickBody: "Выберите шаблон, чтобы заполнить атрибуты, либо начните с нуля.",
      blank: "Пустой продукт",
      blankHint: "Без шаблона — схему атрибутов выберете сами.",
      noTemplates: "В этом пространстве ещё нет шаблонов продуктов.",
      templatesError: "Не удалось загрузить шаблоны.",
      back: "Назад к шаблонам",
      formTitle: "Новый продукт",
      fromTemplate: "Из шаблона: {{name}}",
      sku: "Артикул",
      name: "Название",
      kind: "Вид",
      baseUom: "Базовая единица измерения",
      baseUomPlaceholder: "Выберите единицу…",
      schema: "Схема атрибутов",
      schemaPlaceholder: "Нет",
      schemaLoading: "Загружаются схемы…",
      attributesTitle: "Атрибуты",
      submit: "Создать продукт",
      submitting: "Создаётся…",
      created: "{{sku}} создан. Останется черновиком, пока вы его не активируете.",
      cancel: "Отмена",
      error: "Не удалось создать продукт. Проверьте поля и попробуйте ещё раз.",
    },
    discontinue: {
      title: "Снять продукт с производства",
      body: "Снятые с производства продукты не попадают в новые предложения и планы. В существующих заказах и истории они сохраняются.",
      inUseFound:
        "Продукт входит в действующие спецификации ({{count}}). Если снять его всё равно, они будут ссылаться на снятый с производства компонент.",
      force: "Всё равно снять",
      confirm: "Снять с производства",
      cancel: "Отмена",
    },
  },

  // ---- S2 Карточка продукта ------------------------------------------------
  productDetail: {
    loading: "Загружается продукт…",
    errorTitle: "Не удалось загрузить продукт",
    notFound: "Продукт не найден.",
    header: {
      qrPrint: "Печать QR-карточки",
      qrPrinting: "Готовится…",
      working: "Выполняется…",
      qrDraft: "QR-коды выдаются при вводе продукта в действие.",
      qrUnavailable: "Выгрузка QR-карточки недоступна.",
      activate: "Ввести в действие",
      discontinue: "Снять с производства",
      reactivate: "Вернуть в действие",
      done: {
        activate: "Введён в действие. Продукт можно планировать, продавать и производить.",
        discontinue: "Снят с производства. Остаётся в текущих заказах и в истории.",
        reactivate: "Возвращён в действие. Продукт снова доступен для новых работ.",
      },
    },
    tabsLabel: "Разделы карточки продукта",
    tabs: {
      details: "Сведения",
      attributes: "Атрибуты",
      uom: "Единицы и пересчёты",
      boms: "Спецификации",
      routings: "Маршруты",
      whereUsed: "Где используется",
      cost: "Себестоимость",
      history: "История",
    },
    details: {
      title: "Сведения о продукте",
      sku: "Артикул",
      skuLocked: "Артикул фиксируется после активации продукта.",
      name: "Наименование",
      description: "Описание",
      kind: "Вид",
      baseUom: "Базовая единица",
      fixedHint: "Задано при создании продукта и здесь не изменяется.",
      makeOrBuy: "Производство или закупка",
      tracking: "Прослеживаемость",
      assemblyScrapPct: "Отход при сборке, %",
      purchaseLeadDays: "Срок закупки (дней)",
      manufactureLeadDays: "Срок изготовления (дней)",
      priceUnitQty: "Цена за количество",
      priceUnitQtyHint:
        "Себестоимость и цены указываются за такое количество базовых единиц (например, 1000 м).",
      defaultFacility: "Площадка по умолчанию",
      noFacility: "Без площадки по умолчанию",
      save: "Сохранить сведения",
      saving: "Сохраняется…",
      saved: "Сведения сохранены.",
      error: "Не удалось сохранить сведения.",
      readOnly: "У вас нет прав на изменение этого продукта.",
    },
    attributes: {
      title: "Атрибуты",
      none: "У этого продукта нет атрибутов.",
      noSchema: "Этому продукту не назначена схема атрибутов.",
      save: "Сохранить атрибуты",
      saving: "Сохраняется…",
      saved: "Атрибуты сохранены.",
      error: "Не удалось сохранить атрибуты.",
    },
    uom: {
      title: "Единицы и пересчёты",
      base: "Базовая единица",
      conversionsTitle: "Пересчёты для этого продукта",
      from: "Из",
      to: "В",
      factor: "Коэффициент",
      none: "Пересчётов для этого продукта нет.",
      loading: "Загружаются пересчёты…",
      error: "Не удалось загрузить пересчёты.",
    },
    boms: {
      title: "Спецификации",
      none: "Версий спецификации пока нет.",
      loading: "Загружаются спецификации…",
      error: "Не удалось загрузить спецификации.",
      open: "Открыть",
      phantom: "Фантомная",
      facilityWide: "По всей группе",
      version: "Версия",
    },
    routings: {
      title: "Маршруты",
      none: "Версий маршрута пока нет.",
      loading: "Загружаются маршруты…",
      error: "Не удалось загрузить маршруты.",
      open: "Открыть",
      facility: "Площадка",
      version: "Версия",
    },
    whereUsed: {
      title: "Где используется",
      asComponent: "Используется как компонент",
      asOutput: "Выпускается как продукция",
      none: "Этот продукт не упоминается ни в одной спецификации.",
      loading: "Загружаются ссылки…",
      error: "Не удалось загрузить ссылки.",
      version: "в. {{version}}",
    },
    cost: {
      title: "Нормативная себестоимость",
      standard: "Утверждённая нормативная себестоимость",
      notCosted: "Себестоимость ещё не рассчитана.",
      run: "Из расчёта",
      breakdown: "Расшифровка себестоимости",
      loading: "Загружается себестоимость…",
      error: "Не удалось загрузить себестоимость.",
    },
    history: {
      title: "История изменений",
      none: "Конструкторских изменений не записано.",
      loading: "Загружается история…",
      error: "Не удалось загрузить историю.",
      effectiveFrom: "Действует с {{date}}",
      entityBom: "Спецификация",
      entityRouting: "Маршрут",
      recordedBy: "Записано {{date}} · {{actor}}",
    },
  },

  // ---- S3 Редактор спецификации --------------------------------------------
  bomEditor: {
    title: "Спецификация",
    version: "Версия {{version}}",
    outputQty: "Количество выпуска",
    outputUom: "Единица выпуска",
    nameLabel: "Метка",
    namePlaceholder: "например, post-armour-change",
    phantom: "Фантомная",
    headerTitle: "Партия",
    saveHeader: "Сохранить заголовок",
    savedHeader: "Партия сохранена.",
    savedLines: "Сохранено компонентов: {{count}}.",
    savedOutputs: "Сохранено позиций выпуска: {{count}}.",
    loadingLabel: "Загружается спецификация…",
    errorTitle: "Не удалось загрузить спецификацию",
    emptyLinesTitle: "Компонентов пока нет",
    emptyLinesBody: "Добавьте первый компонент, чтобы собрать эту спецификацию.",
    draftBanner: "Черновик — правьте компоненты и выпуск, затем вводите в действие.",
    readOnlyBanner: "Эта версия в статусе «{{status}}», её нельзя редактировать.",
    // Ввод в действие с датой в будущем (spec 01 §9.16, D-261).
    scheduledBanner: "Запланирована — эта версия вступит в силу {{date}} и пока не действует.",
    createNewVersion: "Создать новую версию",
    creatingVersion: "Создаётся…",
    versionCreated: "Черновик v{{version}} создан как копия v{{from}}.",
    tree: {
      heading: "Компоненты",
      addLine: "Добавить компонент",
      expand: "Развернуть",
      collapse: "Свернуть",
      netOfScrap: "без учёта отходов",
      perOutput: "на выпуск",
      structureUnavailable: "Вложенная структура для этого черновика недоступна.",
      unknownComponent: "Неизвестный компонент",
    },
    inspector: {
      heading: "Свойства строки",
      selectPrompt: "Выберите компонент слева, чтобы его отредактировать.",
      component: "Компонент",
      componentPlaceholder: "Выберите компонент…",
      qty: "Количество",
      uom: "Единица",
      scrapPct: "% отходов",
      consumeAtStep: "Списывается на операции",
      atWoStart: "В начале заказа на производство",
      optional: "Необязательный компонент",
      notes: "Примечания",
      substitutes: "Замены",
      addSubstitute: "Добавить замену",
      substitutePlaceholder: "Выберите замену…",
      priority: "Приоритет",
      qtyFactor: "Коэффициент количества",
      removeSubstitute: "Убрать",
      removeLine: "Убрать компонент",
      save: "Сохранить компоненты",
      saving: "Сохраняется…",
      unsaved: "Несохранённые изменения",
    },
    outputs: {
      heading: "Выпуск",
      coProducts: "Сопутствующие продукты",
      byProducts: "Побочные продукты",
      addCoProduct: "Добавить сопутствующий продукт",
      addByProduct: "Добавить побочный продукт",
      primaryShare: "Основной",
      coProductShare: "Сопутствующие",
      remainder: "{{pct}}% — основному",
      shareBarLabel: "Стоимость партии: {{primary}}% основному продукту, {{total}}% сопутствующим.",
      overAllocated: "На сопутствующие продукты распределено {{total}}% — должно быть менее 100%.",
      valid: "На сопутствующие продукты распределено {{total}}%.",
      product: "Продукт",
      productPlaceholder: "Выберите продукт…",
      qty: "Количество",
      uom: "Единица",
      apportionmentPct: "% распределения",
      nrv: "ЧСР (за ценовую единицу)",
      nrvPriceUnitQty: "Количество в ценовой единице",
      currency: "Валюта",
      nrvRedacted: "ЧСР скрыта — у вас нет доступа к себестоимости.",
      addByProductNoCost: "Для добавления побочных продуктов нужен доступ к себестоимости.",
      removeOutput: "Убрать",
      save: "Сохранить выпуск",
      saving: "Сохраняется…",
      saveBlocked:
        "Перед сохранением распределение на сопутствующие продукты должно быть менее 100%.",
      none: "Кроме основного продукта, ничего не выпускается.",
    },
    activate: {
      button: "Ввести в действие",
      dialogTitle: "Ввести эту версию в действие",
      effectiveFrom: "Действует с",
      note: "Примечание к вводу в действие",
      notePlaceholder: "Почему меняется эта версия? (обязательно)",
      noteRequired: "Примечание к вводу в действие обязательно.",
      pastDate: "Дата начала действия не может быть в прошлом.",
      overlapPreview: "Версия {{version}} будет заменена с {{date}}.",
      conflictPreview: "Версия {{version}} уже покрывает эту дату — выберите дату позже.",
      noOverlap: "Заменять нечего — действующей версии нет.",
      saveFirst: "Сохраните изменения перед вводом в действие.",
      fixApportionment:
        "Исправьте распределение на сопутствующие продукты (в сумме менее 100%) перед вводом в действие.",
      confirm: "Ввести в действие",
      activating: "Вводится в действие…",
      cancel: "Отмена",
      success: "Введено в действие. Эта версия теперь действующая.",
      scheduled: "Ввод в действие запланирован на {{date}}. Эта версия пока не действует.",
    },
    diff: {
      compareAgainst: "Сравнить с",
      selectVersion: "Выберите версию…",
      loading: "Загружается сравнение…",
    },
    picker: {
      change: "Изменить",
      searching: "Идёт поиск…",
      noMatches: "Подходящих продуктов нет.",
      failed: "Не удалось выполнить поиск продуктов. Повторите попытку.",
    },
  },

  // ---- S4 Редактор маршрута ------------------------------------------------
  routing: {
    title: "Маршрут",
    loading: "Загружается маршрут…",
    errorTitle: "Не удалось загрузить маршрут",
    badRoute: "Адрес маршрута неполный. Откройте продукт и выберите версию маршрута.",
    unsaved: "Несохранённые изменения",
    allSaved: "Все изменения сохранены",
    addStep: "Добавить операцию",
    createNewVersion: "Создать новую версию",
    versionCreated: "Черновик v{{version}} создан как копия v{{from}}.",
    readOnlyBanner:
      "Этот маршрут в статусе «{{status}}», его нельзя редактировать. Чтобы внести изменения, создайте новую версию.",
    // Ввод в действие с датой в будущем (spec 01 §9.16, D-261). Мужской род — маршрут.
    scheduledBanner:
      "Запланирован — этот маршрут вступит в силу {{date}} и пока не действует. Его операции нельзя редактировать.",
    consumeWarning:
      "Спецификация списывает компоненты на операции {{steps}}, которой в этом маршруте больше нет.",
    versionsTitle: "Версии",
    stepsTitle: "Операции",
    compareWith: "Сравнить с",
    emptyTitle: "Операций пока нет",
    emptyBody:
      "В этом маршруте нет операций. Добавьте первую, чтобы описать, как изготавливается продукт.",
    emptyCta: "Добавить первую операцию",
    unnamedStep: "Операция без названия",
    leadTime: {
      label: "Время цикла на 1000 м",
      value: "{{minutes}} мин ({{hours}} ч)",
      sub: "по {{count}} операциям",
    },
    save: {
      save: "Сохранить изменения",
      saving: "Сохраняется…",
      error: "Не удалось сохранить операции. Проверьте поля и попробуйте ещё раз.",
      fixSteps: "Исправьте ошибки на операции {{steps}} перед сохранением.",
    },
    columns: {
      step: "№",
      operation: "Операция",
      workCenter: "Рабочий центр",
      times: "Нормы времени",
      actions: "Действия",
    },
    runSummary: {
      line: "Наладка {{setup}} мин · {{run}}",
      perUnit: "{{minutes}} мин/ед.",
      perBatch: "{{minutes}} мин/серия",
    },
    actions: {
      moveUp: "Переместить операцию {{n}} выше",
      moveDown: "Переместить операцию {{n}} ниже",
      view: "Посмотреть",
      edit: "Изменить",
      remove: "Удалить операцию {{n}}",
    },
    flags: {
      outsourced: "По кооперации",
      measured: "Фиксация измерения",
      serial: "Фиксация серийного номера",
      manualIssue: "Ручное списание",
      overlap: "Перекрытие",
      qc: "Точка контроля",
      alternates: "Альтернативных рабочих центров: {{n}}",
      instructions: "Блоков инструкций: {{n}}",
    },
    fields: {
      name: "Название операции",
      namePlaceholder: "например, нанесение ПВХ",
      workCenter: "Рабочий центр",
      setupMinutes: "Минут на наладку",
      runBasis: "База нормирования",
      runMinutesUnit: "Минут на единицу",
      runMinutesBatch: "Минут на серию",
      batchQty: "Размер серии",
      setupKey: "Ключ наладки",
      setupKeyPlaceholder: "например, die:70mm2",
      setupKeyHint: "Одинаковый ключ = нулевая переналадка в расписании.",
      operationScrapPct: "% отходов по операции",
      alternateWorkCenters: "Альтернативные рабочие центры",
      qcControlPoint: "Точка контроля качества",
      qcControlPointPlaceholder: "Идентификатор точки контроля качества",
      qcControlPointHint:
        "Привязка точки контроля вставляет после этой операции контроль качества.",
      captureGroup: "Фиксация в цехе",
      requiresMeasuredCapture: "Требуется фиксация измерения",
      requiresSerialCapture: "Требуется фиксация серийного номера",
      backflush: "Обратное списание компонентов на этой операции",
      canOverlap: "Следующая операция может начаться при частичной передаче",
      outsourced: "Операция по кооперации",
      minCrew: "Минимальный состав бригады",
      outsourceCode: "Код договора кооперации",
      outsourceCodePlaceholder: "Код операции у поставщика",
      instructions: "Технологические инструкции",
    },
    workCenters: {
      loading: "Загружаются рабочие центры…",
      error: "Не удалось загрузить рабочие центры.",
      empty: "На этой площадке нет действующих рабочих центров.",
      placeholder: "Выберите рабочий центр…",
      loadHint: "Показаны только рабочие центры этой площадки.",
      noAlternates: "Других рабочих центров нет.",
    },
    runBasis: {
      per_unit: "На единицу",
      per_batch: "На серию",
    },
    errors: {
      nameRequired: "Введите название операции.",
      workCenterRequired: "Выберите рабочий центр.",
      runMinutesInvalid: "Введите число минут не меньше нуля.",
      batchQtyRequired: "Для операции с нормированием на серию введите её размер.",
      outsourceCodeRequired: "Введите код договора кооперации.",
    },
    instructions: {
      empty: "Блоков инструкций пока нет.",
      textLabel: "Текст инструкции, блок {{n}}",
      imageKey: "Ключ изображения",
      imageKeyPlaceholder: "Ключ объекта S3",
      imageAlt: "Альтернативный текст",
      uploadUnavailable: "Загрузка изображений недоступна. Укажите ключ существующего файла.",
      moveUp: "Переместить блок выше",
      moveDown: "Переместить блок ниже",
      removeBlock: "Удалить блок",
      addText: "Добавить текст",
      addImage: "Добавить изображение",
    },
    drawer: {
      titleNew: "Новая операция",
      titleEdit: "Изменить операцию {{n}}",
      titleView: "Операция {{n}}",
      readOnly:
        "Этот маршрут не является черновиком, поэтому его операции изменить нельзя. Создайте новую версию.",
      close: "Закрыть",
      delete: "Удалить операцию",
      cancel: "Отмена",
      apply: "Применить",
    },
    activate: {
      open: "Ввести в действие",
      title: "Ввести маршрут в действие",
      unsavedWarning:
        "Сохраните изменения перед вводом в действие — вводится сохранённый черновик.",
      noStepsWarning: "Чтобы ввести маршрут в действие, в нём нужна хотя бы одна операция.",
      consumeMismatchWarning:
        "Спецификация списывает компоненты на операции {{steps}}, которой в этом маршруте нет. Ввод в действие всё равно разрешён (версии независимы).",
      supersedePreview: "Версия {{version}} будет заменена с даты начала действия.",
      effectiveFrom: "Действует с",
      effectiveFromHint: "Сегодня или будущая дата. Задним числом не принимается.",
      note: "Примечание к вводу в действие",
      notePlaceholder: "Причина изменения (упрощённая запись извещения об изменении)",
      cancel: "Отмена",
      confirm: "Ввести в действие",
      activating: "Вводится в действие…",
    },
  },

  // ---- S5 Единицы измерения ------------------------------------------------
  uom: {
    title: "Единицы измерения",
    subtitle: "Единицы и пересчёты, используемые в продуктах, спецификациях и маршрутах.",
    unitsTitle: "Единицы",
    conversionsTitle: "Пересчёты",
    colCode: "Код",
    colName: "Название",
    colDimension: "Размерность",
    colBase: "Базовая",
    colPrecision: "Точность",
    colSource: "Источник",
    colStatus: "Статус",
    colActions: "Действия",
    colFrom: "Из",
    colTo: "В",
    colFactor: "Коэффициент",
    baseBadge: "Базовая",
    addUnit: "Добавить единицу",
    addConversion: "Добавить пересчёт",
    edit: "Изменить",
    save: "Сохранить",
    cancel: "Отмена",
    archive: "В архив",
    remove: "Удалить",
    showArchived: "Показать архивные",
    unitCreated: "Единица {{code}} добавлена.",
    unitSaved: "Единица {{code}} сохранена.",
    unitArchived: "Единица {{code}} отправлена в архив.",
    conversionCreated: "Пересчёт добавлен.",
    conversionRemoved: "Пересчёт удалён.",
    archivedHiddenBody: "Все единицы здесь архивные. Включите показ архивных, чтобы их увидеть.",
    codeLabel: "Код",
    nameLabel: "Название",
    dimensionLabel: "Размерность",
    baseLabel: "Базовая единица для своей размерности",
    baseAuto: "Первая единица этой размерности — она становится базовой.",
    precisionLabel: "Знаков после запятой",
    factorLabel: "Коэффициент",
    fromLabel: "Из единицы",
    toLabel: "В единицу",
    selectPlaceholder: "Выберите…",
    factorHint: "qty_to = qty_from × коэффициент. Обратный пересчёт выводится автоматически.",
    sameDimensionHint: "Обе единицы должны быть одной размерности.",
    reciprocal: "1 {{from}} = {{factor}} {{to}}",
    dimension: {
      length: "Длина",
      mass: "Масса",
      count: "Количество",
      area: "Площадь",
      volume: "Объём",
      time: "Время",
      container: "Тара",
    },
    source: {
      vertical_pack: "Пакет",
      custom: "Своя",
    },
    unitsEmptyTitle: "Единиц пока нет",
    unitsEmptyBody: "Добавьте базовую единицу для каждой измеряемой вами размерности.",
    conversionsEmptyTitle: "Пересчётов пока нет",
    conversionsEmptyBody: "Добавьте пересчёт между двумя единицами одной размерности.",
    createFailed: "Не удалось сохранить единицу. Проверьте поля и попробуйте ещё раз.",
    updateFailed: "Не удалось обновить единицу. Попробуйте ещё раз.",
    archiveFailed: "Не удалось отправить единицу в архив. Попробуйте ещё раз.",
    conversionCreateFailed: "Не удалось сохранить пересчёт. Проверьте поля и попробуйте ещё раз.",
    conversionDeleteFailed: "Не удалось удалить пересчёт. Попробуйте ещё раз.",
    inUse:
      "Используется: продуктов — {{products}}, строк спецификаций — {{bomLines}}. В архив отправить нельзя.",
  },

  // ---- S8 Шаблоны ----------------------------------------------------------
  templates: {
    title: "Шаблоны",
    subtitle: "Заготовки для новых продуктов, спецификаций и маршрутов.",
    newTemplate: "Новый шаблон",
    use: "Использовать",
    archive: "В архив",
    viewPayload: "Показать содержимое",
    hidePayload: "Скрыть содержимое",
    showArchived: "Показать архивные",
    created: "Шаблон {{name}} сохранён.",
    archived: "Шаблон {{name}} отправлен в архив.",
    group: {
      product: "Шаблоны продуктов",
      bom: "Шаблоны спецификаций",
      routing: "Шаблоны маршрутов",
    },
    kind: {
      product: "Продукт",
      bom: "Спецификация",
      routing: "Маршрут",
    },
    source: {
      vertical_pack: "Пакет",
      custom: "Свой",
    },
    preview: {
      baseUom: "Базовая единица",
      productKind: "Вид",
      tracking: "Способ учёта",
      makeOrBuy: "Производство / закупка",
      attributes: "Атрибутов: {{count}}",
      steps: "Операций: {{count}}",
      lines: "Компонентов: {{count}}",
      outputUom: "Единица выпуска",
      phantom: "Фантомная",
      none: "Предпросмотр недоступен.",
    },
    useProductNote: "Открываем форму создания продукта с этим шаблоном.",
    useStructureNote: "Выберите продукт, затем создайте из этого шаблона: {{kind}}.",
    form: {
      title: "Новый свой шаблон",
      nameLabel: "Название",
      codeLabel: "Код",
      kindLabel: "Вид",
      modeLabel: "Содержимое",
      modeEmpty: "Пустая заготовка",
      modeSnapshot: "Снимок с существующего",
      sourceProductLabel: "Исходный продукт",
      sourceEntityLabel: "Идентификатор источника",
      sourceEntityHint:
        "Вставьте идентификатор спецификации или маршрута, чтобы снять снимок их содержимого.",
      create: "Сохранить шаблон",
      cancel: "Отмена",
      createFailed: "Не удалось сохранить шаблон. Проверьте поля и попробуйте ещё раз.",
      loadingProducts: "Загружаются продукты…",
    },
    archiveFailed: "Не удалось отправить шаблон в архив. Попробуйте ещё раз.",
    emptyTitle: "Шаблонов пока нет",
    emptyBody: "Сохраните продукт, спецификацию или маршрут как многоразовый шаблон.",
    archivedOnlyTitle: "Все шаблоны здесь архивные",
    archivedOnlyBody: "Включите показ архивных шаблонов или сохраните новый.",
  },
};
